import React, { useEffect, useState } from 'react';
import { fetchNotifications, markNotificationAsRead, markAllNotificationsAsRead } from './notificationsApi';
import { Box, Typography, Paper, List, ListItem, ListItemText, ListItemAvatar, Avatar, Button, Chip, IconButton, CircularProgress } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useTranslation } from 'react-i18next';
import config from './config';

// ✅ FIXED: Helper to extract image from truncated JSON
const getNotificationImage = (notification) => {
  // Priority 1: Order Items Product Image
  if (notification.OrderItems && Array.isArray(notification.OrderItems) && notification.OrderItems.length > 0) {
    const firstProduct = notification.OrderItems[0];
    
    if (firstProduct.ProductImage) {
      let prodImg = firstProduct.ProductImage;

      // Try to parse as JSON first
      if (typeof prodImg === 'string') {
        try {
          const parsed = JSON.parse(prodImg);
          prodImg = parsed;
        } catch (e) {
          // FALLBACK: Extract URLs from truncated JSON string using regex
          const urlMatches = prodImg.match(/https?:\/\/[^\s"]+/g);
          if (urlMatches && urlMatches.length > 0) {
            const validUrl = urlMatches.find((url) => 
              url.includes('/medium/') || url.includes('/thumbnail/') || url.includes('/large/')
            );
            
            if (validUrl) {
              return validUrl;
            }
          }
        }
      }

      // Now check if it's an object
      if (typeof prodImg === 'object' && !Array.isArray(prodImg)) {
        if (prodImg.medium && prodImg.medium.startsWith('http')) {
          return prodImg.medium;
        }
        if (prodImg.thumbnail && prodImg.thumbnail.startsWith('http')) {
          return prodImg.thumbnail;
        }
        if (prodImg.large && prodImg.large.startsWith('http')) {
          return prodImg.large;
        }
      }
    }
  }

  // Priority 2: Direct Image field
  if (notification.Image) {
    let imageData = notification.Image;

    // Try to parse as JSON first
    if (typeof imageData === 'string') {
      try {
        const parsed = JSON.parse(imageData);
        imageData = parsed;
      } catch (e) {
        // FALLBACK: Extract URLs from truncated JSON string
        const urlMatches = imageData.match(/https?:\/\/[^\s"]+/g);
        if (urlMatches && urlMatches.length > 0) {
          const validUrl = urlMatches.find((url) => 
            url.includes('/medium/') || url.includes('/thumbnail/') || url.includes('/large/')
          );
          
          if (validUrl) {
            return validUrl;
          }
        }
      }
    }

    // Check if it's an object
    if (typeof imageData === 'object' && !Array.isArray(imageData)) {
      if (imageData.medium && imageData.medium.startsWith('http')) {
        return imageData.medium;
      }
      if (imageData.thumbnail && imageData.thumbnail.startsWith('http')) {
        return imageData.thumbnail;
      }
      if (imageData.large && imageData.large.startsWith('http')) {
        return imageData.large;
      }
    }

    // Check if it's a direct URL string
    if (typeof imageData === 'string') {
      if (imageData.startsWith('http')) {
        return imageData;
      }
      return `${config.API_BASE_URL}/${imageData.replace(/^\//, '')}`;
    }
  }

  return null;
};

export default function StoreNotifications() {
  const { t, i18n } = useTranslation();
  const [notifications, setNotifications] = useState([]);
  const [user, setUser] = useState(null);
  const [visibleCount, setVisibleCount] = useState(7);
  const [markingAllRead, setMarkingAllRead] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    try {
      const localUser = JSON.parse(localStorage.getItem('user'));
      setUser(localUser);
    } catch {
      setUser(null);
    }
  }, []);

  useEffect(() => {
    if (user && (user.Id || user.id)) {
      setLoading(true);
      fetchNotifications()
        .then(nots => {
          const userId = user.Id || user.id;
          const filtered = Array.isArray(nots)
            ? nots.filter(noti => noti.UserId === userId).sort((a, b) => {
                // Sort: Unread first, then by date
                if (!a.IsRead && b.IsRead) return -1;
                if (a.IsRead && !b.IsRead) return 1;
                const dateA = new Date(a.CreatedTime || 0).getTime();
                const dateB = new Date(b.CreatedTime || 0).getTime();
                return dateB - dateA;
              })
            : [];
          setNotifications(filtered);
        })
        .finally(() => setLoading(false));
    }
  }, [user]);

  const handleMarkAsRead = async (notificationId) => {
    const success = await markNotificationAsRead(notificationId);
    if (success) {
      setNotifications(prev =>
        prev.map(noti =>
          noti.Id === notificationId ? { ...noti, IsRead: true } : noti
        )
      );
    }
  };

  const handleMarkAllAsRead = async () => {
    const userId = user?.Id || user?.id;
    if (!userId) return;
    
    setMarkingAllRead(true);
    const success = await markAllNotificationsAsRead(Number(userId));
    
    if (success) {
      setNotifications(prev =>
        prev.map(noti => ({ ...noti, IsRead: true }))
      );
    }
    setMarkingAllRead(false);
  };

  const unreadCount = notifications.filter(n => !n.IsRead).length;

  if (!user || !(user.Id || user.id)) {
    return (
      <Paper sx={{ p: 2, mb: 3, maxHeight: 400, overflowY: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="h6" gutterBottom sx={{ textAlign: 'center' }}>
          {t('Notifications.title', 'Notifications')}
        </Typography>
        <Typography sx={{ mb: 2, textAlign: 'center' }}>
          {t('account.loginToSeeNotifications', 'Please log in to see your notifications.')}
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Button variant="contained" color="primary" href="/login">
            {t('account.login', 'Go to Login')}
          </Button>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 2, mb: 3, maxHeight: 500, overflowY: 'auto' }}>
      {/* Header with Mark All Read Button */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ textAlign: i18n.language === 'ar' ? 'right' : 'left' }}>
          {t('Notifications.title', 'Notifications')}
          {unreadCount > 0 && (
            <Chip 
              label={`${unreadCount} ${t('Notifications.new', 'new')}`} 
              size="small" 
              color="error" 
              sx={{ ml: 1, fontWeight: 'bold' }} 
            />
          )}
        </Typography>
        {unreadCount > 0 && (
          <Button
            size="small"
            startIcon={markingAllRead ? <CircularProgress size={16} /> : <CheckCircleIcon />}
            onClick={handleMarkAllAsRead}
            disabled={markingAllRead}
            sx={{ fontSize: 12 }}
          >
            {t('Notifications.markAllRead', 'Mark All Read')}
          </Button>
        )}
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <List>
            {notifications.slice(0, visibleCount).map(noti => {
              const imageUrl = getNotificationImage(noti);
              const isOrderType = noti.Type?.toLowerCase().includes('order') && noti.OrderId;

              return (
                <ListItem 
                  key={noti.Id} 
                  alignItems="flex-start" 
                  sx={{ 
                    flexDirection: i18n.language === 'ar' ? 'row-reverse' : 'row',
                    textAlign: i18n.language === 'ar' ? 'right' : 'left',
                    border: '1px solid', 
                    borderColor: !noti.IsRead ? 'primary.main' : 'grey.300',
                    bgcolor: !noti.IsRead ? 'primary.50' : 'transparent',
                    borderRadius: 2, 
                    mb: 1, 
                    p: 1.5,
                    position: 'relative'
                  }}
                >
                  {/* Unread indicator */}
                  {!noti.IsRead && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 8,
                        [i18n.language === 'ar' ? 'left' : 'right']: 8,
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        bgcolor: 'error.main',
                        border: '2px solid white'
                      }}
                    />
                  )}

                  <ListItemAvatar>
                    <Avatar 
                      src={imageUrl} 
                      sx={{ 
                        width: 56, 
                        height: 56,
                        border: !noti.IsRead ? '2px solid' : 'none',
                        borderColor: 'primary.main'
                      }}
                    >
                      {!imageUrl && (noti.Title?.[0] || '?')}
                    </Avatar>
                  </ListItemAvatar>

                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                        <Typography 
                          variant="body1" 
                          sx={{ 
                            fontWeight: !noti.IsRead ? 700 : 600,
                            color: !noti.IsRead ? 'primary.main' : 'text.primary',
                            wordBreak: 'break-word' // ✅ Show full text
                          }}
                        >
                          {noti.Title}
                          {isOrderType ? ` #${noti.OrderId}` : ''}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <>
                        {noti.Subtitle && (
                          <Typography 
                            component="span" 
                            variant="body2" 
                            color="text.primary" 
                            sx={{ display: 'block', mb: 0.5, wordBreak: 'break-word' }} // ✅ Show full text
                          >
                            {noti.Subtitle}
                          </Typography>
                        )}
                        {noti.Message && (
                          <Typography 
                            component="span" 
                            variant="body2" 
                            color="text.secondary" 
                            sx={{ display: 'block', mb: 0.5, wordBreak: 'break-word' }} // ✅ Show full text
                          >
                            {noti.Message}
                          </Typography>
                        )}
                        <Typography component="span" variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {new Date(noti.CreatedTime).toLocaleString()}
                        </Typography>
                        
                        {/* Mark as Read Button */}
                        {!noti.IsRead && (
                          <Button
                            size="small"
                            startIcon={<CheckCircleOutlineIcon />}
                            onClick={() => handleMarkAsRead(noti.Id)}
                            sx={{ mt: 1, fontSize: 11 }}
                          >
                            {t('Notifications.markRead', 'Mark as Read')}
                          </Button>
                        )}
                      </>
                    }
                  />
                </ListItem>
              );
            })}
          </List>

          {visibleCount < notifications.length && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Button 
                variant="outlined" 
                onClick={() => setVisibleCount(c => Math.min(c + 7, notifications.length))}
              >
                {t('Notifications.showMore', 'Show More')} ({notifications.length - visibleCount} {t('common.more', 'more')})
              </Button>
            </Box>
          )}
        </>
      )}
    </Paper>
  );
}
